import React from 'react';

import {
  AssayProcedureMethodInstanceSeedDataConfigEditor,
  validateAssayProcedureMethodInstanceSeedDataConfigValid,
} from 'notebook/worksheets/components/AssayProcedureMethodInstanceAnalysis';
import {
  validateRecipeMultiProcedureRunSeedDataConfigValid,
  RecipeMultiProcedureRunSeedDataConfigEditor,
} from 'studies/typed/process-development/RecipeMultiProcedureRunAnalysis';
import {
  validateRecipeProcedureRunSeedDataConfigValid,
  RecipeProcedureRunSeedDataConfigEditor,
} from 'studies/typed/process-development/RecipeProcedureRunAnalysis';
import FormGroup from 'ui-library/bs/FormGroup';
import lazyInit from 'util/lazyInit';

import {ExampleSeedDataConfigEditor, validateExampleSeedDataConfig} from './ExampleSeedDataConfigEditor';
import {SeedDataConfig, SeedDataConfigType} from './SeedDataTypes';

interface SeedDataConfigMeta<T extends SeedDataConfigType> {
  component: React.FC<{
    value: SeedDataConfig & {type: T};
    onChange: (value: SeedDataConfig & {type: T}) => void;
  }>;
  validate: (config: SeedDataConfig & {type: T}) => boolean;
}

/**
 * Component registry for each SeedDataConfigType.
 *
 * NOTE: We use lazyInit to ensure we don't run into issues with circular dependencies.
 */
const getSeedDataConfigTypeMap = lazyInit<
  Record<SeedDataConfigType, SeedDataConfigMeta<SeedDataConfigType>> & {
    [K in SeedDataConfigType]?: SeedDataConfigMeta<K>;
  }
>(() => ({
  [SeedDataConfigType.EXAMPLE_SEED_DATA]: {
    component: ExampleSeedDataConfigEditor,
    validate: validateExampleSeedDataConfig,
  },
  [SeedDataConfigType.PROCEDURE_RUN]: {
    component: RecipeProcedureRunSeedDataConfigEditor,
    validate: validateRecipeProcedureRunSeedDataConfigValid,
  },
  [SeedDataConfigType.ASSAY_PROCEDURE_METHOD]: {
    component: AssayProcedureMethodInstanceSeedDataConfigEditor,
    validate: validateAssayProcedureMethodInstanceSeedDataConfigValid,
  },
  [SeedDataConfigType.MULTIPLE_PROCEDURE_RUNS]: {
    component: RecipeMultiProcedureRunSeedDataConfigEditor,
    validate: validateRecipeMultiProcedureRunSeedDataConfigValid,
  },
}));

export function isSeedDataConfigValid(seedDataConfig: SeedDataConfig): boolean {
  const {validate} = getSeedDataConfigTypeMap()[seedDataConfig.type];
  return validate(seedDataConfig);
}

interface SeedDataRenderProps {
  seedDataConfig: SeedDataConfig;
  setSeedDataConfig: (newSeedDataConfig: SeedDataConfig) => void;
}

export default function SeedDataRender({
  seedDataConfig,
  setSeedDataConfig,
}: SeedDataRenderProps): JSX.Element {
  const {component: Component} = getSeedDataConfigTypeMap()[seedDataConfig.type];
  return (
    <FormGroup>
      <Component value={seedDataConfig} onChange={setSeedDataConfig} />
    </FormGroup>
  );
}
